import type { FrontendApi, LogoutFlow } from '@ory/client-fetch';

import { handleOryCall, logger } from '@/utils';

interface LogoutResult {
  success: boolean;
  error?: string;
}

/**
 * Logs user out
 * @param ory The Ory client instance
 * @returns Result object with success status and, optionally, error message
 */
const logout = async (ory: FrontendApi): Promise<LogoutResult> => {
  try {
    // Create logout flow using our utility to handle any status code
    const flowResult = await handleOryCall<LogoutFlow>(
      () => ory.createBrowserLogoutFlow({}),
      'Logout flow creation',
    );

    const flowData = flowResult?.data;

    // Check if we got a usable flow data
    if (flowResult.error) {
      throw new Error(
        'Failed to create logout flow: ' +
          (flowResult.error?.message || 'No flow data returned'),
      );
    }

    // Extract logout token
    const logoutToken = flowData?.logout_token;

    if (!logoutToken) {
      throw new Error('CSRF token not found in flow');
    }

    // Update logout flow to log user out
    await handleOryCall<void>(
      () =>
        ory.updateLogoutFlow({
          token: logoutToken,
        }),
      'Logging out',
    );

    if (typeof window !== 'undefined') {
      localStorage.removeItem('auth-context');
      localStorage.removeItem('apollo-client-auth-jwt');
      localStorage.removeItem('auth-machine-state');
      localStorage.removeItem('operationalEntityId');
      localStorage.removeItem('userId');
    }

    return {
      success: true,
    };
  } catch (error) {
    logger.error(`Login OTP error: ${error}`);
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
};

export { logout };
export type { LogoutResult };
